// dito ASK — Rich-Text (Nachtrag 4i): gemeinsames Modul für Teilnahme und Verwaltung.
// Erlaubtes Teilset (identisch mit src/Support/RichText.php):
//   Blöcke  h1, h2, p            mit Klasse rt-center | rt-justify (links = ohne Klasse)
//   Inline  strong, em, br, span mit Klasse rt-color-theme | rt-color-theme-dark | rt-color-muted
// Darstellung und Editor arbeiten ausschließlich mit DOM-Knoten (kein innerHTML mit Nutzerdaten); gespeichert wird
// das serialisierte Teilset. Reiner Text (ohne Block am Anfang) bleibt reiner Text und wird absatzweise dargestellt.

export const BLOCK_TAGS = ['h1', 'h2', 'p'];
export const ALIGN_CLASSES = ['rt-center', 'rt-justify'];
export const COLOR_CLASSES = ['rt-color-theme', 'rt-color-theme-dark', 'rt-color-muted'];
export const COLORS = [
  { key: '', label: 'Standard', title: 'Textfarbe Standard' },
  { key: 'rt-color-theme', label: 'Themenfarbe', title: 'Textfarbe: Themenfarbe der Umfrage' },
  { key: 'rt-color-theme-dark', label: 'Themenfarbe dunkel', title: 'Textfarbe: Themenfarbe dunkel' },
  { key: 'rt-color-muted', label: 'Gedämpft', title: 'Textfarbe: gedämpft' },
];

/** Sieht der Text nach Rich-Text aus (beginnt mit einem erlaubten Block)? */
export function isRich(text) { return /^\s*<(h1|h2|p)(\s|>)/i.test(String(text ?? '')); }

function el(tag, cls) { const e = document.createElement(tag); if (cls) e.className = cls; return e; }

function alignClassOf(node) {
  for (const c of node.classList) if (ALIGN_CLASSES.includes(c)) return c;
  const ta = (node.style && node.style.textAlign) || (node.getAttribute('align') || '').toLowerCase();
  if (ta === 'center') return 'rt-center';
  if (ta === 'justify') return 'rt-justify';
  return '';
}
function colorClassOf(node) { for (const c of node.classList) if (COLOR_CLASSES.includes(c)) return c; return ''; }

/** Inline-Inhalt eines Knotens in das Teilset überführen; liefert neue Knoten. */
function cleanInline(node, out) {
  for (const child of node.childNodes) cleanInlineNode(child, out);
  return out;
}
function cleanInlineNode(node, out) {
  if (node.nodeType === Node.TEXT_NODE) { if (node.nodeValue !== '') out.push(document.createTextNode(node.nodeValue)); return; }
  if (node.nodeType !== Node.ELEMENT_NODE) return;
  const tag = node.tagName.toLowerCase();
  if (tag === 'br') { out.push(el('br')); return; }
  if (tag === 'script' || tag === 'style' || tag === 'template' || tag === 'iframe' || tag === 'object' || tag === 'embed') return;
  const st = node.style || {};
  const bold = tag === 'strong' || tag === 'b' || (st.fontWeight && (st.fontWeight === 'bold' || Number(st.fontWeight) >= 600));
  const italic = tag === 'em' || tag === 'i' || st.fontStyle === 'italic';
  const color = (tag === 'span' || tag === 'font') ? colorClassOf(node) : '';
  let wrap = null, inner = null;
  if (bold) { wrap = inner = el('strong'); }
  if (italic) { const e = el('em'); if (inner) inner.append(e); else wrap = e; inner = e; }
  if (color) { const e = el('span', color); if (inner) inner.append(e); else wrap = e; inner = e; }
  if (wrap) { cleanInline(node, []).forEach(n => inner.append(n)); if (inner.hasChildNodes()) out.push(wrap); return; }
  // sonstige Elemente (a, u, span ohne Farbe, verschachtelte Blöcke …): nur der Inhalt bleibt
  cleanInline(node, out);
}

/**
 * Container (geparstes Dokument oder Editorinhalt) in saubere Blöcke überführen.
 * Liefert ein Array neuer Elemente h1/h2/p mit höchstens einer Ausrichtungsklasse.
 */
export function cleanBlocks(container) {
  const blocks = [];
  let pending = [];
  const flush = () => { if (pending.some(n => n.nodeType !== Node.TEXT_NODE || n.nodeValue.trim() !== '')) { const p = el('p'); pending.forEach(n => p.append(n)); blocks.push(p); } pending = []; };
  for (const node of container.childNodes) {
    let tag = node.nodeType === Node.ELEMENT_NODE ? node.tagName.toLowerCase() : '';
    if (tag === 'div' || tag === 'section' || tag === 'article' || tag === 'blockquote' || tag === 'h3' || tag === 'h4' || tag === 'h5' || tag === 'h6') tag = 'p';
    if (BLOCK_TAGS.includes(tag)) {
      flush();
      const inner = cleanInline(node, []);
      if (!inner.some(n => n.nodeType !== Node.TEXT_NODE || n.nodeValue.trim() !== '')) continue;
      // Browser setzen in leere Absätze ein <br>; ein Block nur aus <br> ist leer
      if (inner.every(n => n.nodeType === Node.ELEMENT_NODE && n.tagName === 'BR')) continue;
      const b = el(tag, alignClassOf(node));
      inner.forEach(n => b.append(n));
      blocks.push(b);
    } else if (tag === 'ul' || tag === 'ol') {
      flush();
      for (const li of node.children) { const p = el('p'); cleanInline(li, []).forEach(n => p.append(n)); if (p.textContent.trim() !== '') blocks.push(p); }
    } else {
      cleanInlineNode(node, pending);
    }
  }
  flush();
  return blocks;
}

function parse(html) { return new DOMParser().parseFromString('<!doctype html><body>' + String(html), 'text/html').body; }

/** Text in Absätze (Leerzeile = neuer Absatz, Zeilenumbruch = <br>). */
export function plainBlocks(text) {
  return String(text ?? '').split(/\n{2,}/).filter(t => t.trim() !== '').map(t => { const p = el('p'); t.split('\n').forEach((l, i) => { if (i) p.append(el('br')); p.append(document.createTextNode(l)); }); return p; });
}

/**
 * Darstellung: liefert DOM-Knoten für Rich- oder reinen Text. opts.headingLevel = HTML-Ebene für „Überschrift 1“
 * (Standard 2, die nächste Ebene für „Überschrift 2“); die Klassen rt-h1/rt-h2 tragen das Aussehen.
 */
export function renderRich(text, opts = {}) {
  const base = Math.min(Math.max(Number(opts.headingLevel) || 2, 1), 5);
  if (!isRich(text)) return plainBlocks(text);
  return cleanBlocks(parse(text)).map(b => {
    const tag = b.tagName.toLowerCase();
    if (tag === 'p') return b;
    const level = tag === 'h1' ? base : base + 1;
    const hx = el('h' + level, ('rt-' + tag + ' ' + b.className).trim());
    while (b.firstChild) hx.append(b.firstChild);
    return hx;
  });
}

/** Rich-Text als reiner Text (Listen, Kürzel, Suche). */
export function plainText(text) {
  if (!isRich(text)) return String(text ?? '');
  return cleanBlocks(parse(text)).map(b => Array.from(b.childNodes).map(n => n.nodeType === Node.ELEMENT_NODE && n.tagName === 'BR' ? '\n' : n.textContent).join('')).join('\n\n').trim();
}

function escape(s) { return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }
function serializeNode(n) {
  if (n.nodeType === Node.TEXT_NODE) return escape(n.nodeValue);
  if (n.nodeType !== Node.ELEMENT_NODE) return '';
  const tag = n.tagName.toLowerCase();
  if (tag === 'br') return '<br>';
  const cls = n.className ? ' class="' + n.className + '"' : '';
  return '<' + tag + cls + '>' + Array.from(n.childNodes).map(serializeNode).join('') + '</' + tag + '>';
}

/**
 * Editorinhalt serialisieren. Ohne Gestaltung (nur Absätze ohne Ausrichtung, ohne Inline-Auszeichnung) entsteht
 * wieder reiner Text, damit unformatierte Inhalte wie bisher gespeichert und übersetzt werden.
 */
export function serialize(container) {
  const blocks = cleanBlocks(container);
  if (!blocks.length) return '';
  const plainOnly = blocks.every(b => b.tagName === 'P' && !b.className && Array.from(b.querySelectorAll('*')).every(x => x.tagName === 'BR'));
  if (plainOnly) return blocks.map(b => Array.from(b.childNodes).map(n => n.nodeType === Node.ELEMENT_NODE ? '\n' : n.nodeValue).join('').replace(/ /g, ' ')).join('\n\n');
  return blocks.map(serializeNode).join('');
}

// ---- Editor ---------------------------------------------------------------------------------------------------

const SENTINEL = { '': '#000001', 'rt-color-theme': '#000002', 'rt-color-theme-dark': '#000003', 'rt-color-muted': '#000004' };
function normColor(v) {
  v = String(v || '').trim().toLowerCase();
  const m = v.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (m) return '#' + [m[1], m[2], m[3]].map(x => Number(x).toString(16).padStart(2, '0')).join('');
  return v;
}

/** Knoten aus einem Vorfahren herauslösen; der Vorfahre wird davor/danach geteilt (für Farbwechsel ohne Verschachtelung). */
function liftOut(node, ancestor) {
  let child = node; while (child.parentNode !== ancestor) child = child.parentNode;
  const after = ancestor.cloneNode(false);
  while (child.nextSibling) after.append(child.nextSibling);
  if (child !== node) { liftOut(node, child); while (node.nextSibling) after.append(node.nextSibling); }
  ancestor.after(node); node.after(after);
  if (!after.hasChildNodes()) after.remove();
  if (!ancestor.hasChildNodes()) ancestor.remove();
}
function unwrap(node) { const p = node.parentNode; while (node.firstChild) p.insertBefore(node.firstChild, node); node.remove(); }

/**
 * richEditor({ name, label, value, help, disabled, required, rows, theme }) → Feldgruppe (.field.field--rich) mit Werkzeugleiste,
 * contenteditable-Fläche und verstecktem <textarea name> (für formValues/showFieldErrors). el.rtSet(text) / el.rtGet().
 */
export function richEditor(spec) {
  const name = spec.name || 'rt';
  const id = 'rt-' + name.replace(/[^a-z0-9_]/gi, '-') + '-' + Math.random().toString(36).slice(2, 7);
  const source = el('textarea', 'rt-source'); if (spec.name) source.name = spec.name; source.hidden = true; source.tabIndex = -1; source.setAttribute('aria-hidden', 'true');
  const area = el('div', 'rt-editor rt'); area.id = id; area.setAttribute('role', 'textbox'); area.setAttribute('aria-multiline', 'true');
  if (spec.rows) area.style.minHeight = (Number(spec.rows) * 1.6 + 1) + 'em';
  if (spec.lang) area.lang = spec.lang;
  if (spec.dir) area.dir = spec.dir;
  if (spec.ariaLabel) area.setAttribute('aria-label', spec.ariaLabel);
  const disabled = !!spec.disabled;
  area.contentEditable = disabled ? 'false' : 'true';
  if (disabled) area.classList.add('rt-editor--disabled');

  function set(text) {
    while (area.firstChild) area.removeChild(area.firstChild);
    const blocks = isRich(text) ? cleanBlocks(parse(text)) : plainBlocks(text);
    if (!blocks.length) { const p = el('p'); p.append(el('br')); blocks.push(p); }
    blocks.forEach(b => area.append(b));
    sync();
  }
  let wrap = null;
  function sync() { const v = serialize(area); if (v !== source.value) { source.value = v; if (wrap) wrap.dispatchEvent(new CustomEvent('rt-change', { detail: { value: v } })); } }
  function get() { sync(); return source.value; }

  function selectionBlocks() {
    const sel = document.getSelection();
    if (!sel || !sel.rangeCount) return [];
    const range = sel.getRangeAt(0);
    if (!area.contains(range.commonAncestorContainer)) return [];
    return Array.from(area.children).filter(c => range.intersectsNode(c));
  }
  function exec(cmd, value) {
    area.focus();
    try { document.execCommand('styleWithCSS', false, 'false'); } catch (e) { /* ignorieren */ }
    try { document.execCommand(cmd, false, value); } catch (e) { /* ignorieren */ }
  }
  function setBlock(tag) {
    const aligns = selectionBlocks().map(b => alignClassOf(b));
    exec('formatBlock', '<' + tag + '>');
    selectionBlocks().forEach((b, i) => { if (aligns[i]) b.className = aligns[i]; });
    normalize(); sync(); updateState();
  }
  function setAlign(cls) {
    const blocks = selectionBlocks();
    if (!blocks.length) return;
    blocks.forEach(b => { b.classList.remove(...ALIGN_CLASSES); if (cls) b.classList.add(cls); });
    sync(); updateState();
  }
  function setColor(key) {
    const sel = document.getSelection();
    if (!sel || !sel.rangeCount || sel.isCollapsed) return;
    exec('foreColor', SENTINEL[key]);
    // Browser erzeugt <font color> (oder span mit style): sofort in Klassen-Spans überführen, Verschachtelung auflösen
    for (const f of Array.from(area.querySelectorAll('font, span'))) {
      const c = normColor(f.getAttribute('color') || (f.style && f.style.color));
      const hit = Object.entries(SENTINEL).find(([, v]) => v === c);
      if (!hit) continue;
      const span = el('span', hit[0]);
      while (f.firstChild) span.append(f.firstChild);
      f.replaceWith(span);
      for (const inner of Array.from(span.querySelectorAll('span'))) if (colorClassOf(inner)) unwrap(inner);
      let anc = span.parentElement;
      while (anc && anc !== area) { const next = anc.parentElement; if (anc.tagName === 'SPAN' && colorClassOf(anc)) liftOut(span, anc); anc = next; }
      if (!hit[0]) unwrap(span);
    }
    normalize(); sync(); updateState();
  }
  /** Lose Inline-Inhalte direkt im Editor (z. B. nach Löschen aller Blöcke) in einen Absatz heben. */
  function normalize() {
    let loose = Array.from(area.childNodes).filter(n => n.nodeType === Node.TEXT_NODE ? n.nodeValue !== '' : !BLOCK_TAGS.includes(n.tagName.toLowerCase()) && !['DIV', 'UL', 'OL', 'BLOCKQUOTE'].includes(n.tagName));
    if (loose.length) { const p = el('p'); loose[0].before(p); loose.forEach(n => p.append(n)); }
    if (!area.firstChild) { const p = el('p'); p.append(el('br')); area.append(p); }
  }

  // Werkzeugleiste
  const buttons = [];
  function tb(label, title, onClick, opts = {}) {
    const b = el('button', 'rt-tb' + (opts.cls ? ' ' + opts.cls : '')); b.type = 'button'; b.title = title; b.setAttribute('aria-label', title); b.textContent = label; b.disabled = disabled;
    b.dataset.rt = opts.key || '';
    b.addEventListener('mousedown', ev => ev.preventDefault());
    b.addEventListener('click', onClick);
    buttons.push(b); return b;
  }
  const sep = () => el('span', 'rt-sep');
  const toolbar = el('div', 'rt-toolbar'); toolbar.setAttribute('role', 'toolbar'); toolbar.setAttribute('aria-label', 'Textgestaltung');
  toolbar.append(
    tb('Ü1', 'Überschrift 1', () => setBlock('h1'), { key: 'h1' }), tb('Ü2', 'Überschrift 2', () => setBlock('h2'), { key: 'h2' }), tb('Text', 'Normaler Text', () => setBlock('p'), { key: 'p' }), sep(),
    tb('⇤', 'Linksbündig', () => setAlign(''), { key: 'left' }), tb('↔', 'Zentriert', () => setAlign('rt-center'), { key: 'rt-center' }), tb('≡', 'Blocksatz', () => setAlign('rt-justify'), { key: 'rt-justify' }), sep(),
    tb('F', 'Fett', () => { exec('bold'); sync(); updateState(); }, { key: 'bold', cls: 'rt-tb--bold' }), tb('K', 'Kursiv', () => { exec('italic'); sync(); updateState(); }, { key: 'italic', cls: 'rt-tb--italic' }), sep(),
    ...COLORS.map(c => tb('A', c.title, () => setColor(c.key), { key: 'color:' + c.key, cls: 'rt-tb--color ' + (c.key || 'rt-color-default') })),
  );
  function updateState() {
    const blocks = selectionBlocks();
    const b0 = blocks[0];
    const tag = b0 ? b0.tagName.toLowerCase() : '';
    const align = b0 ? alignClassOf(b0) : '';
    let bold = false, italic = false;
    try { bold = document.queryCommandState('bold'); italic = document.queryCommandState('italic'); } catch (e) { /* ignorieren */ }
    for (const b of buttons) {
      const k = b.dataset.rt;
      const on = (k === tag) || (k === 'left' && b0 && !align) || (k === align) || (k === 'bold' && bold) || (k === 'italic' && italic);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    }
  }
  area.addEventListener('input', () => { normalize(); sync(); });
  area.addEventListener('keyup', updateState);
  area.addEventListener('mouseup', updateState);
  area.addEventListener('focus', () => { try { document.execCommand('defaultParagraphSeparator', false, 'p'); } catch (e) { /* ignorieren */ } updateState(); });
  area.addEventListener('paste', (ev) => {
    // Nur Text einfügen (Formatierungen fremder Quellen bleiben draußen)
    const text = ev.clipboardData && ev.clipboardData.getData('text/plain');
    if (text === null || text === undefined) return;
    ev.preventDefault();
    exec('insertText', text);
    normalize(); sync();
  });

  const label = el('label'); label.htmlFor = id; label.textContent = spec.label || '';
  if (spec.required) { const r = el('span', 'req'); r.setAttribute('aria-hidden', 'true'); r.textContent = ' *'; label.append(r); }
  const err = el('p', 'field-error'); err.dataset.errorFor = name; err.hidden = true;
  wrap = el('div', 'field field--rich' + (spec.compact ? ' field--rich-compact' : ''));
  if (spec.label) wrap.append(label);
  wrap.append(toolbar, area, source);
  if (spec.help) { const hp = el('p', 'help'); hp.textContent = spec.help; wrap.append(hp); }
  wrap.append(err);
  wrap.rtSet = set; wrap.rtGet = get;
  if (spec.theme) applyThemeColors(wrap, spec.theme);
  set(spec.value ?? '');
  return wrap;
}

/** Farben der Umfrage (GET …/design) als CSS-Variablen auf einen Container legen (CSSOM, kein style-Attribut). */
export function applyThemeColors(container, design) {
  if (!container || !design) return;
  if (design.primary) container.style.setProperty('--rt-theme', design.primary);
  if (design.primary_hover) container.style.setProperty('--rt-theme-dark', design.primary_hover);
}
